import React, { useState, useEffect } from "react";
import {
  FaUser,
  FaTrash,
  FaHome,
} from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { LuLogOut, LuMapPin, LuShoppingBag, LuUser } from "react-icons/lu";
import { FaLocationCrosshairs } from "react-icons/fa6";
import Breadcrumb from "../components/Breadcrumb";
import { Link } from "react-router-dom";
import Config from "../API/Config";
import useLogout from "../utils/logout";
import {
  AddAddress,
  getAddressById,
  UpdateAddress,
  DeleteAddress,
} from "../API/AddressAPI";
import { useFormik } from "formik";
import * as Yup from "yup";
import FormLoader from "../components/FormLoader";
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete';
import { PuffLoader } from "react-spinners";
import { toast } from 'sonner';
import Swal from 'sweetalert2';

const Address = () => {
  const [openModal, setOpenModal] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [editAddress, setEditAddress] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [addressesLoading, setAddressesLoading] = useState(true);
  const [locationLoading, setLocationLoading] = useState(false);
  const [disabledFields, setDisabledFields] = useState({
    AddressLine1: false,
    City: false,
    State: false,
    Country: false,
    Pincode: false,
  });

  const logout = useLogout();
  const UserName = Config.getUserName();
  const UserEmail = Config.getUserEmail();

  // 🔹 Fetch all addresses
  useEffect(() => {
    fetchAddress();
  }, []);

  const fetchAddress = async () => {
    setAddressesLoading(true);
    try {
      const res = await getAddressById();
      if (res) {
        setAddresses(res || []);
      }
    } catch (err) {
      console.error("Error fetching address", err);
    } finally {
      setAddressesLoading(false);
    }
  };

  // Reset disabled fields when modal opens/closes or edit changes
  useEffect(() => {
    if (openModal) {
      if (editAddress) {
        setDisabledFields({
          AddressLine1: !!editAddress.AddressLine1,
          City: !!editAddress.City,
          State: !!editAddress.State,
          Country: !!editAddress.Country,
          Pincode: !!editAddress.Pincode,
        });
      } else {
        setDisabledFields({
          AddressLine1: false,
          City: false,
          State: false,
          Country: false,
          Pincode: false,
        });
      }
    }
  }, [openModal, editAddress]);

  // 🔹 Parse address components
  const parseAddressComponents = (result) => {
    const components = result.address_components;
    const geometry = result.geometry;

    let name = "";
    let area = "";
    let city = "";
    let state = "";
    let country = "";
    let pincode = "";

    components.forEach((comp) => {
      if (comp.types.includes("premise") || comp.types.includes("street_number")) {
        name = comp.long_name;
      } else if (comp.types.includes("route")) {
        area = comp.long_name;
      } else if (
        comp.types.includes("sublocality") ||
        comp.types.includes("sublocality_level_1")
      ) {
        if (!area) {
          area = comp.long_name;
        } else {
          area = `${area}, ${comp.long_name}`;
        }
      } else if (comp.types.includes("locality")) {
        city = comp.long_name;
      } else if (comp.types.includes("administrative_area_level_1")) {
        state = comp.long_name;
      } else if (comp.types.includes("country")) {
        // Map country names to codes
        const countryCode = comp.short_name;
        if (countryCode === "IN" || comp.long_name === "India") {
          country = "IN";
        } else if (countryCode === "US" || comp.long_name === "United States") {
          country = "US";
        } else {
          country = "IN"; // Default to India
        }
      } else if (comp.types.includes("postal_code")) {
        pincode = comp.long_name;
      }
    });

    const latVal = typeof geometry.location.lat === 'function' ? geometry.location.lat() : geometry.location.lat;
    const lngVal = typeof geometry.location.lng === 'function' ? geometry.location.lng() : geometry.location.lng;

    return {
      name,
      address: result.formatted_address,
      area,
      city,
      state,
      country,
      lat: latVal,
      lng: lngVal,
      pincode,
    };
  };

  // 🔹 Handle place selection from autocomplete
  const handlePlaceSelect = async (place) => {
    try {
      const results = await geocodeByPlaceId(place.value.place_id);
      if (results && results.length > 0) {
        const parsed = parseAddressComponents(results[0]);
        let street = [parsed.name, parsed.area].filter(Boolean).join(', ');
        if (!street) street = parsed.address.split(',')[0];

        formik.setValues({
          ...formik.values, // Preserve LandMark
          AddressLine1: street,
          City: parsed.city,
          Area: parsed.area,
          State: parsed.state,
          Country: parsed.country,
          Pincode: parsed.pincode,
          Lat: parsed.lat.toString(),
          Lng: parsed.lng.toString(),
        });

        setDisabledFields({
          AddressLine1: !!street,
          City: !!parsed.city,
          State: !!parsed.state,
          Country: !!parsed.country,
          Pincode: !!parsed.pincode,
        });
      }
    } catch (error) {
      console.error("Error selecting place:", error);
    }
  };

  // 🔹 Handle current location
  const handleCurrentLocation = () => {
    if (navigator.geolocation) {
      setLocationLoading(true);
      navigator.geolocation.getCurrentPosition(async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        try {
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyCo5zCVQSeC9qaDTElGlakIt5tOfbuFXW4`
          );
          const data = await response.json();
          if (data.status === "OK" && data.results.length > 0) {
            const result = data.results[0];
            const parsed = parseAddressComponents(result);
            let street = [parsed.name, parsed.area].filter(Boolean).join(', ');
            if (!street) street = parsed.address.split(',')[0];

            formik.setValues({
              ...formik.values, // Preserve LandMark
              AddressLine1: street,
              City: parsed.city,
              Area: parsed.area,
              State: parsed.state,
              Country: parsed.country,
              Pincode: parsed.pincode,
              Lat: lat.toString(),
              Lng: lng.toString(),
            });

            setDisabledFields({
              AddressLine1: !!street,
              City: !!parsed.city,
              State: !!parsed.state,
              Country: !!parsed.country,
              Pincode: !!parsed.pincode,
            });
          }
        } catch (error) {
          console.error("Error fetching address from coords:", error);
        } finally {
          setLocationLoading(false);
        }
      }, (error) => {
        console.error("Geolocation error:", error);
        setLocationLoading(false);
      });
    } else {
      console.error("Geolocation not supported.");
    }
  };

  // 🔹 Formik Setup
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      AddressLine1: editAddress?.AddressLine1 || "",
      LandMark: editAddress?.LandMark || "",
      City: editAddress?.City || "",
      State: editAddress?.State || "",
      Country: editAddress?.Country || "IN",
      Pincode: editAddress?.Pincode || "",
      Lat: editAddress?.Lat || "",
      Lng: editAddress?.Lng || "",
    },
    validationSchema: Yup.object({
      AddressLine1: Yup.string().required("Required"),
      LandMark: Yup.string().required("Required"),
      City: Yup.string().required("Required"),
      State: Yup.string().required("Required"),
      Country: Yup.string().required("Required"),
      Pincode: Yup.string().required("Required"),
      // Lat and Lng not required in validation to allow manual entry without coords (defaults to 0 if empty)
    }),
    onSubmit: async (values, { resetForm }) => {
      setFormLoading(true);
      try {
        const payload = {
          ...values,
          Lat: values.Lat || "0.0000",
          Lng: values.Lng || "0.0000",
        };
        let res;
        if (editAddress) {
          res = await UpdateAddress({ Id: editAddress.Id, ...payload });
        } else {
          res = await AddAddress(payload);
        }

        if (res.status === true) {
          setOpenModal(false);
          setEditAddress(null);
          fetchAddress();
          resetForm();
          // toast.success(editAddress ? "Address updated successfully" : "Address added successfully");
        }
      } catch (err) {
        console.error("Submit Error", err);
      } finally {
        setFormLoading(false);
      }
    },
  });

  // Handle Delete with confirmation
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Address?',
      text: 'Are you sure you want to delete this address? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const res = await DeleteAddress({ Id: id });
        if (res?.status) {
          toast.success("Address has been deleted.");
          fetchAddress();
        }
      } catch (err) {
        console.error("Delete error", err);
        toast.error("Something went wrong.");
      }
    }
  };

  return (
    <div>
      {/* Breadcrumb */}
      <Breadcrumb title="Saved Address" path="Saved Address" />

      <div className="container mx-auto px-4 md:py-20 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Left Sidebar */}
          <div className="bg-[#f9f9f9] rounded-lg overflow-hidden">
            <div className="relative md:h-36 h-24 bg-theme2">
              <div className="bg-pink-700 uppercase absolute left-1/2 md:-bottom-11 -bottom-10 transform -translate-x-1/2 
                                text-white md:w-24 w-20 md:h-24 h-20 border-4 border-white flex items-center justify-center 
                                rounded-full text-5xl shadow-md">
                {UserName ? UserName.charAt(0) : <FaUser />}
              </div>
            </div>

            {/* User Info */}
            <div className="flex flex-col items-center mt-12 px-4">
              <h3 className="font-semibold text-lg">{UserName}</h3>
              <p className="text-sm text-gray-500">{UserEmail}</p>
            </div>

            {/* Menu */}
            <ul
              className="md:mt-8 mt-4 md:pb-6 pb-2 flex flex-row md:flex-col 
                            md:space-y-2 space-x-2 md:space-x-0 md:pl-4
                            overflow-x-auto md:overflow-visible flex-nowrap"
            >
              <Link
                to={"/profile"}
                className="flex items-center whitespace-nowrap md:text-lg text-sm py-1.5 px-2 md:gap-3 gap-1 text-gray-600 cursor-pointer hover:text-theme2 transition"
              >
                <LuUser className="md:text-lg text-sm" /> Edit Profile
              </Link>
              <Link
                to={"/my-order"}
                className="flex items-center whitespace-nowrap md:text-lg text-sm py-1.5 px-2 md:gap-3 gap-1 text-gray-600 cursor-pointer hover:text-theme2 transition"
              >
                <LuShoppingBag className="md:text-lg text-sm" /> My Order
              </Link>
              <Link
                to={"/my-address"}
                className="flex items-center whitespace-nowrap md:text-lg text-sm py-1.5 px-2 md:gap-3 gap-1 text-theme2 font-semibold cursor-pointer md:border-b-0 md:border-r-4 border-theme2"
              >
                <LuMapPin className="md:text-lg text-sm" /> My Address
              </Link>
              <Link
                onClick={logout}
                className="flex items-center whitespace-nowrap md:text-lg text-sm py-1.5 px-2 md:gap-3 gap-1 text-gray-600 cursor-pointer hover:text-theme2 transition"
              >
                <LuLogOut className="md:text-lg text-sm" /> Log Out
              </Link>
            </ul>
          </div>

          {/* Right Section */}
          <div className="lg:col-span-3 bg-[#f9f9f9] rounded-lg shadow md:p-6 p-4">
            <div className="loader-line"></div>
            <h2 className="md:text-2xl text-xl font-semibold pb-3 mb-4 mt-2">
              My Address
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              {addressesLoading ? (
                <div className="col-span-2 flex justify-center items-center">
                  <PuffLoader color="#83442B" size={60} />
                </div>
              ) : (
                <>
                  {addresses.map((addr) => (
                    <div
                      key={addr.Id}
                      className="border rounded-lg relative bg-white"
                    >
                      <div className="flex items-center gap-2 p-3.5 py-4 border-b text-lg font-semibold mb-2">
                        <FaHome className="text-theme2" /> {addr.LandMark || "Home"}
                      </div>
                      <div className="p-4">
                        <p className="text-darktext text-lg">
                          {addr.AddressLine1}, {addr.City}, {addr.State},{" "}
                          {addr.Country}-{addr.Pincode}
                        </p>
                      </div>
                      <div className="absolute top-4 right-4 flex gap-2">
                        <button
                          className="text-sm px-3 py-1 rounded-full bg-[#e1b37d1f] text-theme2 flex items-center gap-1"
                          onClick={() => {
                            setEditAddress(addr);
                            setOpenModal(true);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="text-sm text-red-600"
                          onClick={() => handleDelete(addr.Id)}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Add New Address */}
                  <div className="border rounded-lg p-4 flex items-center justify-center">
                    <button
                      onClick={() => {
                        setEditAddress(null);
                        setOpenModal(true);
                      }}
                      className="bg-theme2 w-full md:w-auto hover:bg-black text-white px-6 py-2 rounded-md font-semibold"
                    >
                      Add New Address
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Address Modal */}
      {openModal && (
        <div className="fixed inset-0 px-4 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          {locationLoading && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
              <PuffLoader color={"#83442B"} size={60} />
            </div>
          )}
          <div className="bg-white rounded-lg shadow-lg w-full max-w-xl max-h-[90vh] overflow-y-auto p-4 md:p-6 relative my-4">
            {formLoading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] rounded-lg">
                <PuffLoader color={"#83442B"} size={60} />
              </div>
            )}

            <button
              onClick={() => {
                setOpenModal(false);
                setEditAddress(null);
              }}
              className="absolute top-2 right-2 md:top-4 md:right-4 text-gray-500 hover:text-gray-700 z-20 p-1"
            >
              <IoClose size={20} className="md:hidden" />
              <IoClose size={22} className="hidden md:block" />
            </button>

            <h2 className="text-lg md:text-xl font-semibold text-darktext mb-4 md:mb-6 pr-8">
              {editAddress ? "Edit Address" : "Add New Address"}
            </h2>

            <form
              className="grid grid-cols-1 gap-3 md:gap-4"
              onSubmit={formik.handleSubmit}
            >
              {/* Search for Address */}
              <div className="col-span-1">
                <label className="block font-medium mb-1 text-darktext text-sm md:text-base">
                  Search for Address
                </label>
                <GooglePlacesAutocomplete
                  apiKey="AIzaSyCo5zCVQSeC9qaDTElGlakIt5tOfbuFXW4"
                  autocompletionRequest={{
                    componentRestrictions: { country: 'in' },
                  }}
                  debounce={500}
                  selectProps={{
                    onChange: handlePlaceSelect,
                    placeholder: 'Enter your address...',
                    styles: {
                      input: (provided) => ({
                        ...provided,
                        padding: '10px 10px 10px 0',
                        width: '100%',
                        textAlign: 'left',
                        color: '#4B5563',
                        borderRadius: '0.375rem',
                        outline: 'none',
                      }),
                      option: (provided) => ({
                        ...provided,
                        color: '#333',
                        textAlign: 'left',
                      }),
                    },
                  }}
                />
              </div>

              {/* Use Current Location Button */}
              <div className="col-span-1 flex justify-center mb-2 md:mb-4">
                <button
                  type="button"
                  onClick={handleCurrentLocation}
                  className="flex items-center gap-2 bg-theme2 text-white px-3 py-2 md:px-4 md:py-2 rounded hover:bg-black text-sm md:text-base"
                >
                  <FaLocationCrosshairs className="text-sm" /> Use Current Location
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                <div>
                  <label className="block font-medium mb-1 text-darktext text-sm md:text-base">
                    Address
                  </label>
                  <input
                    type="text"
                    name="AddressLine1"
                    placeholder="Enter Address"
                    className="w-full border rounded-md px-3 py-2 md:py-2.5 focus:outline-none text-sm md:text-base"
                    value={formik.values.AddressLine1}
                    onChange={formik.handleChange}
                    disabled={disabledFields.AddressLine1}
                  />
                  {formik.touched.AddressLine1 && formik.errors.AddressLine1 && (
                    <p className="text-red-500 text-xs md:text-sm mt-1">
                      {formik.errors.AddressLine1}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block font-medium mb-1 text-darktext text-sm md:text-base">
                    Landmark
                  </label>
                  <input
                    type="text"
                    name="LandMark"
                    placeholder="Enter Landmark"
                    className="w-full border rounded-md px-3 py-2 md:py-2.5 focus:outline-none text-sm md:text-base"
                    value={formik.values.LandMark}
                    onChange={formik.handleChange}
                  />
                  {formik.touched.LandMark && formik.errors.LandMark && (
                    <p className="text-red-500 text-xs md:text-sm mt-1">
                      {formik.errors.LandMark}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block font-medium mb-1 text-darktext text-sm md:text-base">
                    City
                  </label>
                  <input
                    type="text"
                    name="City"
                    placeholder="Enter City"
                    className="w-full border rounded-md px-3 py-2 md:py-2.5 focus:outline-none text-sm md:text-base"
                    value={formik.values.City}
                    onChange={formik.handleChange}
                    disabled={disabledFields.City}
                  />
                  {formik.touched.City && formik.errors.City && (
                    <p className="text-red-500 text-xs md:text-sm mt-1">{formik.errors.City}</p>
                  )}
                </div>
                <div>
                  <label className="block font-medium mb-1 text-darktext text-sm md:text-base">
                    State
                  </label>
                  <input
                    type="text"
                    name="State"
                    placeholder="Enter State"
                    className="w-full border rounded-md px-3 py-2 md:py-2.5 focus:outline-none text-sm md:text-base"
                    value={formik.values.State}
                    onChange={formik.handleChange}
                    disabled={disabledFields.State}
                  />
                  {formik.touched.State && formik.errors.State && (
                    <p className="text-red-500 text-xs md:text-sm mt-1">{formik.errors.State}</p>
                  )}
                </div>
                <div>
                  <label className="block font-medium mb-1 text-darktext text-sm md:text-base">
                    Country
                  </label>
                  <select
                    name="Country"
                    className="w-full border rounded-md px-3 py-2 md:py-2.5 focus:outline-none text-sm md:text-base"
                    value={formik.values.Country}
                    onChange={formik.handleChange}
                    disabled={disabledFields.Country}
                  >
                    <option value="">Select Country</option>
                    <option value="IN">India (IN)</option>
                    <option value="US">United States (US)</option>
                  </select>
                  {formik.touched.Country && formik.errors.Country && (
                    <p className="text-red-500 text-xs md:text-sm mt-1">
                      {formik.errors.Country}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block font-medium mb-1 text-darktext text-sm md:text-base">
                    Zip
                  </label>
                  <input
                    type="text"
                    name="Pincode"
                    placeholder="Enter your zip"
                    className="w-full border rounded-md px-3 py-2 md:py-2.5 focus:outline-none text-sm md:text-base"
                    value={formik.values.Pincode}
                    onChange={formik.handleChange}
                    disabled={disabledFields.Pincode}
                  />
                  {formik.touched.Pincode && formik.errors.Pincode && (
                    <p className="text-red-500 text-xs md:text-sm mt-1">
                      {formik.errors.Pincode}
                    </p>
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex flex-row justify-end mt-4 md:mt-6 gap-2 md:gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setOpenModal(false);
                    setEditAddress(null);
                  }}
                  className="px-4 py-2 md:px-6 md:py-2 rounded-full text-gray-600 bg-gray-100 hover:bg-gray-200 text-sm md:text-base"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="px-4 py-2 md:px-6 md:py-2 bg-theme2 text-white rounded-full hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
                >
                  {formLoading ? "Saving..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Address;

